import React from "react"
import { semantic, foundations } from '@lexody/design-tokens';
import Icon from './Icon';
import LoadingSpinner from './LoadingSpinner';

const { radius, spacing, border } = foundations;

function resolveVariant(props) {
  if (props.variant) return props.variant;
  if (props.alert === true) return 'destructive';
  if (props.primary === true) return 'primary';
  return 'secondary';
}

function getAppearance(props) {
  const variant = resolveVariant(props);
  const disabled = props.disabled === true || props.inactive === true;

  if (disabled) {
    return {
      backgroundColor: semantic.greyscale.surface.disabled,
      borderColor: semantic.greyscale.border.disabled,
      color: semantic.greyscale.text.disabled,
      boxShadow: 'none',
    };
  }

  switch (variant) {
    case 'primary':
      return {
        backgroundColor: semantic.primary.surface.default,
        borderColor: 'transparent',
        color: semantic.greyscale.text.negative,
        boxShadow: foundations.shadow.buttonPrimary.web,
      };
    case 'destructive':
      return {
        backgroundColor: semantic.error.surface.default,
        borderColor: 'transparent',
        color: semantic.greyscale.text.negative,
        boxShadow: '0 4px 14px rgba(244, 143, 177, 0.35)',
      };
    case 'ghost':
      return {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: semantic.primary.surface.default,
        boxShadow: 'none',
      };
    case 'secondary':
    default:
      return {
        backgroundColor: semantic.primary.surface.subtle,
        borderColor: semantic.primary.surface.default,
        color: semantic.primary.surface.default,
        boxShadow: foundations.shadow.buttonSecondary.web,
      };
  }
}

class StandardButton extends React.Component {
  buttonText() {
    if (this.props.loading === true) {
      const variant = resolveVariant(this.props);
      const spinnerVariant = variant === 'secondary' ? 'primary' : 'white';
      return <LoadingSpinner size={22} variant={spinnerVariant} />;
    }

    const iconName = this.props.icon;
    const text = this.props.text;

    if (!iconName) {
      return text;
    }

    const isSmall = this.props.size === 'small';
    const appearance = getAppearance(this.props);
    const icon = (
      <Icon
        name={iconName}
        size={isSmall ? 15 : 17}
        color={appearance.color}
        solid={this.props.iconSolid !== false}
      />
    );

    if (!text) {
      return icon;
    }

    if (this.props.iconRight === true) {
      return (
        <>
          <span>{text}</span>
          {icon}
        </>
      );
    }

    return (
      <>
        {icon}
        <span>{text}</span>
      </>
    );
  }

  render() {
    const appearance = getAppearance(this.props);
    const isSmall = this.props.size === 'small';
    const isLarge = this.props.size === 'large';
    const fullWidth = this.props.fullWidth === true;
    const isLoading = this.props.loading === true;
    const isDisabled = this.props.disabled === true || this.props.inactive === true || isLoading;
    const hasIcon = Boolean(this.props.icon);

    return (
      <button
        className={isLoading ? 'standardButton--loading' : ''}
        disabled={isDisabled}
        aria-busy={isLoading}
        style={{
          backgroundColor: appearance.backgroundColor,
          color: appearance.color,
          border: `${border.width.focus}px solid ${appearance.borderColor}`,
          borderRadius: radius.full,
          fontSize: isSmall ? 14 : 15,
          fontWeight: 700,
          paddingTop: spacing['2'],
          paddingBottom: spacing['2'],
          paddingLeft: isSmall ? spacing['5'] : spacing['6'],
          paddingRight: isSmall ? spacing['5'] : spacing['6'],
          maxWidth: isLarge ? 325 : 'unset',
          minWidth: isLarge ? '100%' : 'unset',
          width: fullWidth ? '100%' : 'auto',
          cursor: isDisabled ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: hasIcon ? spacing['2'] : 0,
          minHeight: isSmall ? 40 : 44,
          boxShadow: appearance.boxShadow,
          marginTop: this.props.style && this.props.style.marginTop,
          position: 'relative',
          overflow: 'hidden',
        }}
        onClick={() => {
          if (!isDisabled) {
            this.props.onClick();
          }
        }}
      >
        {this.buttonText()}
      </button>
    );
  }
}

export default StandardButton;
